import React from 'react';
import classes from './PostCardError.module.scss';
import { PostItemFragment, PostStatus } from '../../../models/graphql';
import { PostScheduleHelpers } from '../../../modules/Publish/PostSchedule/postScheduleHelpers';
import { PostQueueErrorEnum } from '../../../constants/post';
import { CardOrientationEnum, PostError } from '../BasePostCard/BasePostCard.view';
import { DuplicateOrReschedulePostHandler } from '../../../modules/Publish/PostSchedule/PostSchedule.controller';
import { useSettings } from '../../../hooks/useSettings';
import { useNavigate } from 'react-router-dom';
import { ReadMore } from '../../containers/ReadMore';
import { IconsEnum } from '../../../constants/icons';
import { Button } from '../../actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { usePauseSettings } from '../../../hooks/usePauseSettings';
import classNames from 'classnames';

export type PostCardErrorViewProps = {
  post: PostItemFragment;
  orientation: CardOrientationEnum;
  isPostStatusUnqueued?: boolean;
  reschedulePostHandler?: DuplicateOrReschedulePostHandler;
};

export const PostCardErrorView = ({
  post,
  orientation,
  isPostStatusUnqueued,
  reschedulePostHandler
}: PostCardErrorViewProps): JSX.Element => {
  const { selectedWorkspaceSettings } = useSettings();
  const navigate = useNavigate();
  const { isSocialProfilePausedForCategory } = usePauseSettings();

  const isProfilePausedForCategory = isSocialProfilePausedForCategory(post.socialProfile.id, post.category.id);

  const error: PostError | undefined =
    post.status === PostStatus.FAILED
      ? PostScheduleHelpers.getPostMessaging(
          PostQueueErrorEnum.ERROR,
          () => {
            if (reschedulePostHandler) reschedulePostHandler(post, 'reschedule');
          },
          post.failedMessage || undefined
        )
      : selectedWorkspaceSettings?.queuesPaused
      ? PostScheduleHelpers.getPostMessaging(PostQueueErrorEnum.WORKSPACE_PAUSED, () =>
          navigate({ pathname: '/settings/pause' })
        )
      : isProfilePausedForCategory
      ? PostScheduleHelpers.getPostMessaging(PostQueueErrorEnum.SOCIAL_PROFILE_CATEGORY_PAUSED, () =>
          navigate({ pathname: '/settings/pause' })
        )
      : post.category.seasonallyPaused
      ? PostScheduleHelpers.getPostMessaging(PostQueueErrorEnum.CATEGORY_SEASONALLY_PAUSED, () =>
          navigate({ pathname: '/publish/categories' })
        )
      : isPostStatusUnqueued
      ? PostScheduleHelpers.getPostMessaging(
          PostQueueErrorEnum.STATUS_UNQUEUED,
          () =>
            navigate(
              { pathname: '/publish/plan' },
              { state: { category: post.category, profile: post.socialProfile } }
            ),
          `No Timeslots for the "${post.category.name}" Category and "${post.socialProfile.displayName}" Profile`
        )
      : undefined;

  const errorMessageWrapperClasses = classNames('grid-x');
  const errorMessageContainerClasses = classNames('cell ', {
    auto: orientation !== CardOrientationEnum.PORTRAIT,
    'small-12': orientation === CardOrientationEnum.PORTRAIT
  });
  const buttonContainerClasses = classNames('cell', {
    shrink: orientation !== CardOrientationEnum.PORTRAIT,
    'small-12 margin-top-small-12': orientation === CardOrientationEnum.PORTRAIT
  });
  const errorMessageClasses = classNames(classes.ErrorMessage, {
    [classes.ErrorMessageWithReadMore]: orientation === CardOrientationEnum.LANDSCAPE
  });

  return (
    <>
      {error && (
        <div className={classes.ErrorMessageContainer}>
          <div className={errorMessageWrapperClasses}>
            <div className={errorMessageContainerClasses}>
              <div className={errorMessageClasses}>
                <div className={'grid-x'}>
                  {orientation === CardOrientationEnum.PORTRAIT ? (
                    <div>{error.message}</div>
                  ) : (
                    <ReadMore
                      readMoreKey={`readMore-${post.id}`}
                      icon={IconsEnum.exclamation}
                      text={`${error.message}`}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className={buttonContainerClasses}>
              <Button
                type={'button'}
                variant={'hollow'}
                size={'small'}
                color={ButtonColorsEnum.alert}
                action={error.action}
                expanded={true}>
                {error.actionLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
